/**
 * NavMenu - Navigation menu component for page headers.
 * Renders the Explore dropdown and a "Get the app" button.
 */
import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { ChevronDown, MapPin, Sparkles, Smartphone, Menu, X } from "lucide-react";

export default function NavMenu() {
  const navigate = useNavigate();

  // Desktop dropdown state
  const [exploreOpen, setExploreOpen] = useState(false);

  // Mobile menu state
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileExploreOpen, setMobileExploreOpen] = useState(false);

  // User location for "Nearby Waters"
  const [userLocation, setUserLocation] = useState(null);
  const [userState, setUserState] = useState(null);

  const exploreRef = useRef(null);

  // Get user location on mount
  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const { latitude, longitude } = position.coords;
          setUserLocation({ latitude, longitude });
          try {
            const response = await axios.get(`${API}/geocode/reverse`, {
              params: { lat: latitude, lon: longitude }
            });
            if (response.data?.state) setUserState(response.data.state);
          } catch (error) { /* ignore */ }
        },
        () => {},
        { timeout: 10000, maximumAge: 300000 }
      );
    }
  }, []);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (exploreRef.current && !exploreRef.current.contains(event.target)) setExploreOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Navigate to nearby waters
  const handleNearbyWaters = () => {
    const params = new URLSearchParams();
    if (userLocation) {
      params.set('lat', userLocation.latitude.toString());
      params.set('lng', userLocation.longitude.toString());
      params.set('zoom', '10');
      params.set('view', 'map');
    }
    if (userState) params.set('state', userState);
    navigate(`/destinations${params.toString() ? '?' + params.toString() : ''}`);
    setExploreOpen(false);
    setMobileMenuOpen(false);
  };

  return (
    <div className="flex items-center">
      {/* Desktop Navigation */}
      <nav className="hidden md:flex items-center gap-1">
        {/* Explore Dropdown */}
        <div className="relative" ref={exploreRef}>
          <button
            onClick={() => setExploreOpen(!exploreOpen)}
            className="flex items-center gap-1 px-3 py-2 rounded-md text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors"
            data-testid="nav-explore-btn"
          >
            Explore
            <ChevronDown className={`w-4 h-4 transition-transform ${exploreOpen ? 'rotate-180' : ''}`} />
          </button>

          {exploreOpen && (
            <div className="absolute top-full right-0 mt-2 w-56 bg-background rounded-xl shadow-xl border border-border overflow-hidden z-50">
              <div className="py-2">
                <button
                  onClick={handleNearbyWaters}
                  className="w-full px-4 py-3 flex items-center gap-3 hover:bg-muted/50 transition-colors text-left"
                  data-testid="nav-nearby-waters"
                >
                  <MapPin className="w-5 h-5 text-accent" />
                  <div>
                    <p className="font-medium text-foreground text-sm">Nearby Waters</p>
                    <p className="text-xs text-muted-foreground">Find fishing spots near you</p>
                  </div>
                </button>
                <button
                  onClick={() => { navigate('/ai-guide'); setExploreOpen(false); }}
                  className="w-full px-4 py-3 flex items-center gap-3 hover:bg-muted/50 transition-colors text-left"
                  data-testid="nav-ai-guide"
                >
                  <Sparkles className="w-5 h-5 text-accent" />
                  <div>
                    <p className="font-medium text-foreground text-sm">Trip Planner</p>
                    <p className="text-xs text-muted-foreground">Get personalized recommendations</p>
                  </div>
                </button>
              </div>
            </div>
          )}
        </div>

        <Button
          size="sm"
          className="ml-1 rounded-full bg-accent text-accent-foreground hover:bg-accent/90"
          onClick={() => navigate("/app")}
          data-testid="nav-get-app"
        >
          <Smartphone className="w-4 h-4 mr-2" />
          Get the app
        </Button>
      </nav>

      {/* Mobile Menu Button */}
      <button
        onClick={(e) => {
          e.stopPropagation();
          setMobileMenuOpen(!mobileMenuOpen);
        }}
        className="md:hidden p-2 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors"
        data-testid="nav-mobile-btn"
      >
        <Menu className="w-5 h-5" />
      </button>

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div
          className="fixed inset-0 bg-black/50 md:hidden mobile-nav-overlay"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Mobile Menu Panel */}
      <div
        className={`fixed top-0 right-0 h-screen w-80 max-w-[85vw] bg-background shadow-2xl transform transition-transform duration-300 md:hidden mobile-nav-panel ${
          mobileMenuOpen ? 'translate-x-0' : 'translate-x-full pointer-events-none'
        }`}
        data-testid="nav-mobile-panel"
      >
        <div className="flex flex-col h-full">
          {/* Mobile Menu Header */}
          <div className="flex items-center justify-between p-4 border-b border-border">
            <span className="font-serif text-lg font-medium">Menu</span>
            <button
              onClick={() => setMobileMenuOpen(false)}
              className="p-2 rounded-md hover:bg-muted transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Mobile Menu Content */}
          <div className="flex-1 overflow-y-auto py-4">
            {/* Explore Section */}
            <div className="px-4 mb-2">
              <button
                onClick={() => setMobileExploreOpen(!mobileExploreOpen)}
                className="w-full flex items-center justify-between py-3 text-foreground font-medium"
              >
                Explore
                <ChevronDown className={`w-5 h-5 transition-transform ${mobileExploreOpen ? 'rotate-180' : ''}`} />
              </button>

              {mobileExploreOpen && (
                <div className="pl-4 space-y-1 pb-2">
                  <button
                    onClick={handleNearbyWaters}
                    className="w-full flex items-center gap-3 py-2 text-muted-foreground hover:text-foreground transition-colors"
                  >
                    <MapPin className="w-4 h-4 text-accent" />
                    Nearby Waters
                  </button>
                  <button
                    onClick={() => { navigate('/ai-guide'); setMobileMenuOpen(false); }}
                    className="w-full flex items-center gap-3 py-2 text-muted-foreground hover:text-foreground transition-colors"
                  >
                    <Sparkles className="w-4 h-4 text-accent" />
                    Trip Planner
                  </button>
                </div>
              )}
            </div>

            <div className="border-t border-border mx-4 my-4" />

            <div className="px-4">
              <button
                onClick={() => { navigate('/app'); setMobileMenuOpen(false); }}
                className="w-full flex items-center gap-3 py-3 text-accent font-medium"
                data-testid="nav-mobile-get-app"
              >
                <Smartphone className="w-5 h-5 text-accent" />
                Get the app
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
